import { Awaitable } from '@nucleus/lib-function';
import { getText } from '@nucleus/lib-shape';
import { isPrefixedKey } from '@nucleus/ncu-key-prefix';
import { CircleIconOhNoFace, CircleIconPost } from '@nucleus/post-theme-elements';
import { SectionActionOfType } from '@nucleus/types/web';
import { RichText, useWebHostingApi } from '@nucleus/web-theme';
import { buildTransparentColorFromCssVariable, media, ThemeLifeV1Button } from '@nucleus/web-theme-elements';
import { useMutation } from '@tanstack/react-query';
import React, { useState } from 'react';
import styled from 'styled-components';
import { cornerStyleInterpolationFactory } from '../lib/cornerStyle';
import { SectionButton } from './Button';

// Copied from launcher in: apps/launcher/src/shared/constants/legacy.js
export const EmailRegex = /^[A-Z0-9_%+-]+(?:\.[A-Z0-9_%+-]+)*@[A-Z0-9-]+(?:\.[A-Z0-9-]+)*\.[A-Z]{2,}$/i;

export type SubscribeSectionAction = SectionActionOfType<'subscribe'>;

type SubscribeActionProps = {
  action: SubscribeSectionAction;
};

type SubscribeFormState = {
  firstName: string;
  lastName: string;
  email: string;
};

const InitialFormState: SubscribeFormState = {
  firstName: '',
  lastName: '',
  email: '',
};

const getSubscribeCopy = (action: SubscribeSectionAction) => {
  return {
    firstNamePlaceholder: getText(action.firstNamePlaceholder),
    lastNamePlaceholder: getText(action.lastNamePlaceholder),
    emailPlaceholder: getText(action.emailPlaceholder),
    submitButtonText: getText(action.submitButtonText),
    successMessage: getText(action.successMessage),
    errorMessage: getText(action.errorMessage),
    disclaimerText: action.disclaimerText ?? [],
  };
};

const useSubscribeMutation = () => {
  const api = useWebHostingApi();
  const baseMutation = useMutation(api.postHub.post.subscribe.mutationOptions());
  return useMutation({
    mutationFn: async ({ destination, values }: { destination: string | undefined; values: SubscribeFormState }) => {
      if (destination === undefined || !isPrefixedKey(undefined, destination)) {
        throw new Error('Destination is required');
      }
      return await baseMutation.mutateAsync({
        email: values.email,
        entityId: destination,
        firstName: values.firstName,
        lastName: values.lastName,
      });
    },
  });
};

export const SubscribeActionForm = ({ action }: SubscribeActionProps): JSX.Element => {
  const subscribeMutation = useSubscribeMutation();

  const renderMissingEntityWarning = !isPrefixedKey(undefined, action.destination);
  const renderSuccessMessage = subscribeMutation.isSuccess && !renderMissingEntityWarning;
  const renderErrorMessage = subscribeMutation.error && !subscribeMutation.isSuccess && !renderMissingEntityWarning;
  const renderSubscribeForm = !renderMissingEntityWarning && !renderSuccessMessage && !renderErrorMessage;

  const copy = getSubscribeCopy(action);

  return (
    <>
      {renderMissingEntityWarning && (
        <MessageCard>
          <IconContainer>
            <CircleIconOhNoFace />
          </IconContainer>
          <MessageText className="theme-text-paragraph3">
            Subscribe is currently unavailable or has not been configured.
          </MessageText>
        </MessageCard>
      )}
      {renderSuccessMessage && (
        <MessageCard>
          <IconContainer>
            <CircleIconPost />
          </IconContainer>
          <MessageText className="theme-text-paragraph3">{copy.successMessage}</MessageText>
        </MessageCard>
      )}
      {renderErrorMessage && (
        <ErrorCard>
          <IconContainer>
            <CircleIconOhNoFace />
          </IconContainer>
          <MessageText className="theme-text-paragraph3">{copy.errorMessage}</MessageText>
          <ThemeLifeV1Button.Alternate type="button" onClick={() => subscribeMutation.reset()}>
            Retry
          </ThemeLifeV1Button.Alternate>
        </ErrorCard>
      )}
      {renderSubscribeForm && (
        <SubscribeForm
          action={action}
          onSubmit={async (formState) => {
            await subscribeMutation.mutateAsync({
              destination: action.destination,
              values: formState,
            });
          }}
        />
      )}
    </>
  );
};

type SubscribeFormProps = {
  onSubmit: (formState: SubscribeFormState) => Awaitable<void>;
  action: SubscribeSectionAction;
};
const SubscribeForm = ({ action, onSubmit }: SubscribeFormProps) => {
  const [formState, setFormState] = useState<SubscribeFormState>(InitialFormState);

  const isFormValid =
    formState.firstName.trim().length > 0 && formState.lastName.trim().length > 0 && EmailRegex.test(formState.email);

  const handleFieldChange = (field: keyof SubscribeFormState) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormState((previous) => ({ ...previous, [field]: event.target.value }));
  };

  const submitMutation = useMutation({
    mutationFn: async () => {
      if (!isFormValid || submitMutation.isPending) {
        return;
      }
      await onSubmit(formState);
    },
  });

  const renderNameFields = EmailRegex.test(formState.email);

  const formCopy = getSubscribeCopy(action);

  return (
    <>
      <StyledForm
        onSubmit={(e) => {
          e.preventDefault();
          void submitMutation.mutateAsync();
        }}
      >
        <FormField>
          <FormInput
            id="subscribe-email"
            type="email"
            className="theme-text-paragraph3"
            required
            value={formState.email}
            onChange={handleFieldChange('email')}
            autoComplete="email"
            placeholder={formCopy.emailPlaceholder}
          />
        </FormField>
        {renderNameFields && (
          <FieldRow>
            <FormField>
              <FormInput
                id="subscribe-first-name"
                type="text"
                className="theme-text-paragraph3"
                required
                value={formState.firstName}
                onChange={handleFieldChange('firstName')}
                autoComplete="given-name"
                placeholder={formCopy.firstNamePlaceholder}
              />
            </FormField>

            <FormField>
              <FormInput
                id="subscribe-last-name"
                type="text"
                className="theme-text-paragraph3"
                required
                value={formState.lastName}
                onChange={handleFieldChange('lastName')}
                autoComplete="family-name"
                placeholder={formCopy.lastNamePlaceholder}
              />
            </FormField>
          </FieldRow>
        )}

        <SubmitButton displayAs={action.displayAs} type="submit" loading={submitMutation.isPending}>
          {formCopy.submitButtonText}
        </SubmitButton>
        {formCopy.disclaimerText !== undefined && (
          <DisclaimerRichText>
            <RichText nodes={formCopy.disclaimerText} />
          </DisclaimerRichText>
        )}
      </StyledForm>
    </>
  );
};

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  max-width: 46rem;
`;

const DisclaimerRichText = styled.div`
  margin: 0;
  line-height: 1.4;
  color: inherit;
  opacity: 0.75;
  width: 80%;
  margin: 0 auto;
  text-align: center;

  & * {
    font-size: 1.4rem;
    opacity: 0.75;
  }
`;

const FieldRow = styled.div`
  display: flex;
  gap: 1.2rem;
  flex-direction: row;

  & > * {
    flex: 1;
    min-width: 10rem;
  }

  ${media.tabletPortraitAndDown`
    flex-direction: column;
  `}
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const FormInput = styled.input`
  padding: 10px 12px;
  border: 1px solid rgba(128, 128, 128, 0.3);
  border-radius: 8px;
  color: inherit;
  background: transparent;
  outline: none;
  transition: border-color 0.15s ease;
  height: 5.4rem;
  border-width: 1.5px;
  padding-left: 2rem;
  min-width: 10rem;
  background-color: var(--color-light);
  color: var(--color-dark);

  border-radius: ${cornerStyleInterpolationFactory({
    cornerStyle1: '0px',
    cornerStyle2: '1rem',
    cornerStyle3: '5.4rem',
  })};

  &::placeholder {
    color: inherit;
    opacity: 0.5;
    padding-left: 0rem;
  }

  &:focus {
    border-color: var(--color-button-primary-background, #333);
  }
`;

const SubmitButton = styled(SectionButton)`
  margin-top: 1.2rem;
`;

const IconContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  & > svg {
    width: 5.4rem;
    margin-bottom: 1.2rem;
    flex-shrink: 0;

    ${media.tabletPortraitAndDown`
      width: 4rem;
      margin-bottom: 0.8rem;
    `}
  }
`;

const MessageText = styled.p`
  text-align: center;
`;

const MessageCard = styled.div`
  background: ${buildTransparentColorFromCssVariable('--color-section-text', 0.06)};
  border-radius: ${cornerStyleInterpolationFactory({
    cornerStyle1: '0px',
    cornerStyle2: '1.4rem',
    cornerStyle3: '4rem',
  })};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  padding: 6rem;
  max-width: 68rem;
  background-color: var(--color-section-text);
  color: var(--color-block-background);

  ${media.tabletPortraitAndDown`
    padding: 2.6rem;
    padding-top: 3.5rem;
  `}
`;

const ErrorCard = styled(MessageCard)`
  padding-bottom: 4rem;

  ${media.tabletPortraitAndDown`
    padding-top: 4rem;
    padding-bottom: 2rem;
  `}
`;
